import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AcceptPage = () => {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading, valid, accepted, already_accepted, invalid, error
  const [data, setData] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const checkToken = async () => {
      try {
        const response = await axios.get(`${API}/accept/${token}`);
        if (response.data.valid) {
          if (response.data.status === "accepted") {
            setStatus("already_accepted");
          } else {
            setStatus("valid");
          }
          setData(response.data);
        } else {
          setStatus("invalid");
        }
      } catch (err) {
        setStatus("invalid");
      }
    };
    checkToken();
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const response = await axios.post(`${API}/accept/${token}`);
      if (response.data.status === "success") {
        setStatus("accepted");
        setData({ ...data, name: response.data.name });
      } else if (response.data.status === "already_accepted") {
        setStatus("already_accepted");
      }
    } catch (err) {
      setStatus("error");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <main className="bg-void min-h-screen flex items-center justify-center px-6" data-testid="accept-page">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.03)_0%,_transparent_70%)]" />
      
      <motion.div 
        className="relative z-10 max-w-lg w-full"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Loading State */}
        {status === "loading" && (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin mx-auto mb-6" />
            <p className="font-manrope text-zinc-400">Verifying your invitation...</p>
          </div>
        )}

        {/* Valid - Show Accept Button */}
        {status === "valid" && (
          <div className="text-center">
            <p className="font-mono text-xs tracking-[0.3em] text-gold-500 mb-4">YOU'VE BEEN APPROVED</p>
            <h1 className="font-playfair text-5xl md:text-6xl text-bone mb-6">THE ROOM</h1>
            <p className="font-mono text-[10px] tracking-[0.2em] text-zinc-600 mb-12">BY SOVEREIGN</p>
            
            <div className="glass-card p-10 mb-10">
              <p className="font-manrope text-lg text-zinc-300 mb-4">
                Welcome, <span className="text-bone">{data?.name}</span>
              </p>
              <p className="font-manrope text-zinc-500 mb-8 leading-relaxed">
                Your application has been reviewed and approved. Click below to confirm your attendance at THE ROOM in Las Vegas, December 2026.
              </p>
              
              <motion.button
                onClick={handleAccept}
                disabled={accepting}
                className="btn-primary w-full group flex items-center justify-center gap-3"
                data-testid="accept-btn"
                whileHover={{ scale: accepting ? 1 : 1.02 }}
                whileTap={{ scale: accepting ? 1 : 0.98 }}
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    Accept My Invitation
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </motion.button>
            </div>
            
            <p className="font-mono text-[10px] text-zinc-600">
              This invitation is non-transferable.
            </p>
          </div>
        )}

        {/* Accepted State */}
        {status === "accepted" && (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="w-24 h-24 rounded-full border-2 border-gold-500 flex items-center justify-center mx-auto mb-8"
            >
              <CheckCircle className="w-12 h-12 text-gold-500" />
            </motion.div>
            
            <p className="font-mono text-xs tracking-[0.3em] text-gold-500 mb-4">CONFIRMED</p>
            <h1 className="font-playfair text-4xl md:text-5xl text-bone mb-6">You're In.</h1>
            
            <div className="glass-card p-10 mb-10">
              <p className="font-manrope text-lg text-bone mb-4">
                Welcome to THE ROOM, {data?.name}.
              </p>
              <p className="font-manrope text-zinc-400 mb-6 leading-relaxed">
                Your place has been secured. We'll be in touch with final details as the event approaches.
              </p>
              
              <div className="border-t border-white/10 pt-6 mt-6">
                <p className="font-mono text-[10px] tracking-[0.2em] text-zinc-600 mb-2">EVENT</p>
                <p className="font-manrope text-bone">Las Vegas, NV · December 2026</p>
              </div>
            </div>
            
            <Link 
              to="/"
              className="font-mono text-xs tracking-[0.15em] text-zinc-500 hover:text-gold-500 transition-colors"
            >
              RETURN TO SITE
            </Link>
          </div>
        )}

        {/* Already Accepted */}
        {status === "already_accepted" && (
          <div className="text-center">
            <div className="w-24 h-24 rounded-full border-2 border-gold-500/50 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-12 h-12 text-gold-500/50" />
            </div>
            
            <p className="font-mono text-xs tracking-[0.3em] text-zinc-500 mb-4">ALREADY CONFIRMED</p>
            <h1 className="font-playfair text-4xl text-bone mb-6">You're Already In</h1>
            
            <p className="font-manrope text-zinc-400 mb-10 leading-relaxed">
              You've already accepted your invitation to THE ROOM. We'll be in touch with details as the event approaches.
            </p>
            
            <Link 
              to="/"
              className="font-mono text-xs tracking-[0.15em] text-zinc-500 hover:text-gold-500 transition-colors"
            >
              RETURN TO SITE
            </Link>
          </div>
        )}

        {/* Invalid Token */}
        {(status === "invalid" || status === "error") && (
          <div className="text-center">
            <div className="w-24 h-24 rounded-full border-2 border-red-500/50 flex items-center justify-center mx-auto mb-8">
              <XCircle className="w-12 h-12 text-red-500/50" />
            </div>
            
            <p className="font-mono text-xs tracking-[0.3em] text-red-500/70 mb-4">INVALID LINK</p>
            <h1 className="font-playfair text-4xl text-bone mb-6">Invitation Not Found</h1>
            
            <p className="font-manrope text-zinc-400 mb-10 leading-relaxed">
              This invitation link is invalid or has expired. If you believe this is an error, please contact us.
            </p>
            
            <div className="space-y-4">
              <Link 
                to="/"
                className="btn-secondary inline-block"
              >
                Return to Site
              </Link>
              <p className="font-mono text-[10px] text-zinc-600">
                Questions? info@sovereigngroup.io
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </main>
  );
};

export default AcceptPage;
